import React, {useEffect, useState} from 'react';
import {Controller} from 'react-hook-form';
import {
    FormError,
    FormLabel,
    getInputClassNames,
    getInputWrapperClass,
    getWholeDivWrapperClass,
    SUFFIX_CLASS,
} from "./sharedStyles.tsx";
import {SimpleSelectOption} from "../../../api/ApiTypes.ts";
import {ChevronDown} from 'lucide-react';
import {DataSource, FormComponentProps, ParentController, SelectComponent} from '../DynamicFormTypes.ts';
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useFieldBlurHandler} from "../hooks/OnFieldBlurHanlder.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";

interface SelectComponentProps extends FormComponentProps<SelectComponent> {
    disabled?: boolean;
    parentController?: ParentController;
}

export const SelectRenderer: React.FC<SelectComponentProps> = ({ component, disabled = false, parentController = undefined }) => {
    const { formMethods } = useFormMethods()
    const {
        fieldKey,
        dataSource,
        provided
    } = component;


    const [options, setOptions] = useState<SimpleSelectOption[]>([]);

    useEffect(() => {
        // Handle different data sources
        if (dataSource === DataSource.PROVIDED && provided && Array.isArray(provided)) {
            // Use the provided options directly
            setOptions(provided);
        }
        else {
            console.error("Only Provided acceptted at this time for select component", component);
        }
    }, [dataSource]);

    if (!dataSource) {
        return (
            <div className="p-4 border border-red-300 bg-red-50 rounded text-red-700">
                <p className="text-sm font-medium">Error: Select component requires dataSource</p>
            </div>
        );
    }

    const { handleFieldBlur } = useFieldBlurHandler({
        component
    });

    const renderInput = ({ field, fieldState }: ParentController) => {
        // Get base input classes for select input
        let selectClasses = `${getInputClassNames(component, fieldState)} pr-10 appearance-none`;
        return (
            <div className={getWholeDivWrapperClass(component)}>
                <FormLabel component={component} />

                <div className={getInputWrapperClass(component)}>

                    <div className="flex">
                        <div className="flex-grow relative">
                            <select
                                id={fieldKey}
                                className={disabled ? selectClasses.replace("bg-white", "bg-gray-50") : selectClasses}
                                {...field}
                                value={field.value || ''}
                                onChange={(_e)=> {
                                    field.onChange(_e)
                                    field.onBlur();
                                }}
                                onBlur={(_e)=> {
                                    handleFieldBlur();
                                }}
                                disabled={disabled}
                            >
                                <option value="">{disabled ? "-" : "Select an option"}</option>
                                {options.map((option) => (
                                    <option
                                        key={`${fieldKey}-option-${option.value}`}
                                        value={JSON.stringify(option)}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {disabled ? null :
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                </div>
                            }
                        </div>
                        {component.suffix &&
                            <span className={SUFFIX_CLASS}>{component.suffix}</span>
                        }
                    </div>

                </div>
                <FormError fieldState={fieldState} component={component} />
            </div>
        );
    }

    if (parentController) {
        return renderInput(parentController);
    }

    const validate = useValidationRules({ component });

    return (
        <Controller
            name={fieldKey}
            control={formMethods.control}
            rules={validate}
            render={({ field, fieldState }) => renderInput({ field, fieldState })}
        />
    );
}

export const Select = React.memo(SelectRenderer);